import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Plus, BellOff } from 'lucide-react';
import { useChatStore } from '../store/chatStore';
import { useChatUiStore } from '../store/chatUiStore';
import chatService from '../services/chatService';
import { ensureDm, activateChannel } from '../utils/chatOpen';
import { setChannelMuted, leaveChannel, deleteOrphanedDm } from '../services/chatRowActions';
import { isChannelMuted } from '../utils/chatAlerts';
import { channelDisplay, dmOther, isCompleteEmail, dmSubjectId } from './chatHelpers';
import UserAvatar from './ui/UserAvatar';
import AvatarStatusBadge from './ui/AvatarStatusBadge';
import ChannelAvatar from './ui/ChannelAvatar';
import NewChannelModal from './NewChannelModal';
import { useAuthStore } from '../store/authStore';
import { isLimitedPlan } from '../services/personalSyncConsent';
import ChatRowContextMenu, { leavingDestroysChannel } from './ChatRowContextMenu';
import { ConfirmModal } from './ui/Modal';
import useDelayedVisible from '../hooks/useDelayedVisible';
import './Chat.css';

/**
 * Discord-style conversation list: DMs, groups, and owned channels as avatar
 * rows with an unread badge in the bottom-right corner. Selecting a row sets the
 * active conversation, which ChatMessageView renders.
 *
 * The conversation search lives in the sidebar header (same
 * `sidebar-panel-search-row` as Collections): a partial value filters this
 * list, and a *complete email* triggers a debounced people lookup
 * (chatService.searchUsers, exact-email only) whose matches appear in a
 * dropdown under the input — picking one starts/opens the DM. Ctrl/Cmd+F
 * focuses the input while the panel is mounted. "New channel" is the footer
 * button (FTP-sidebar style), which flips `newChannelOpen` to open
 * NewChannelModal rendered here.
 */
export default function ChatSidebar() {
  const { t } = useTranslation();
  const channels = useChatStore((s) => s.channels);
  const channelsLoaded = useChatStore((s) => s.channelsLoaded);
  const activeChannelId = useChatStore((s) => s.activeChannelId);
  const unreadByChannel = useChatStore((s) => s.unreadByChannel);
  const currentUserId = useChatStore((s) => s.currentUserId);
  const setActiveChannel = useChatStore((s) => s.setActiveChannel);
  const clearUnread = useChatStore((s) => s.clearUnread);
  const upsertChannel = useChatStore((s) => s.upsertChannel);

  const showListSkeleton = useDelayedVisible(!channelsLoaded, 300, 500);

  const filter = useChatUiStore((s) => s.filter);
  const setFilter = useChatUiStore((s) => s.setFilter);
  const newChannelOpen = useChatUiStore((s) => s.newChannelOpen);
  // Free owns no channels of its own; DMs and invitations are unaffected.
  //
  // `user?.plan` first, because `isLimitedPlan(null)` is true and `user` is
  // legitimately null for the whole cold-start window (authStore: the
  // persisted session can be missing). Without it a Team account with cleared
  // storage is told channels are a paid feature until /auth/me lands.
  const planUser = useAuthStore((s) => s.user);
  const ownsNoChannels = Boolean(planUser?.plan) && isLimitedPlan(planUser);
  const setNewChannelOpen = useChatUiStore((s) => s.setNewChannelOpen);

  // ── People lookup (moved here from the old ChatTitleBarSearch) ────────────
  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const debounceRef = useRef(null);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const emailMode = isCompleteEmail(filter);
  // `open` (not just `emailMode`) drives the dropdown so it can actually be
  // dismissed; `loading` lets us tell "searching" apart from "no user found".
  const showDropdown = open && emailMode;

  // Exact-email only, debounced 250ms (mirrors the old popover).
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!emailMode) {
      setResults([]);
      setLoading(false);
      setOpen(false);
      return undefined;
    }
    let cancelled = false;
    setResults([]);
    setError(null);
    setLoading(true);
    setOpen(true);
    debounceRef.current = setTimeout(() => {
      chatService
        .searchUsers(filter.trim())
        .then((users) => {
          if (cancelled) return; // a newer query superseded this one
          setResults(Array.isArray(users) ? users : []);
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setResults([]);
          setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(debounceRef.current);
    };
  }, [filter, emailMode]);

  // Ctrl/Cmd+F focuses the filter instead of the browser find bar.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Close the dropdown on outside click. Closing `open` (rather than clearing
  // results) genuinely hides it; the lookup effect reopens it when the query
  // changes to a new email.
  useEffect(() => {
    if (!showDropdown) return undefined;
    const onDown = (e) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showDropdown]);

  const clearSearch = () => {
    setFilter('');
    setResults([]);
    setError(null);
    setOpen(false);
  };

  const openDm = async (userId) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const channel = await ensureDm(userId);
      activateChannel(channel.id);
      clearSearch();
    } catch (e) {
      // Surface the failure and keep the query + dropdown open so the user
      // sees the message and can retry.
      setError(e.response?.data?.error || 'Could not start conversation');
    } finally {
      setBusy(false);
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      clearSearch();
    } else if (e.key === 'Enter' && results[0]) {
      e.preventDefault();
      openDm(results[0].id);
    }
  };

  const sorted = useMemo(
    () => [...channels].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)),
    [channels]
  );

  // A complete-email query drives the DM dropdown, not the list filter, so
  // leave the list unfiltered in that case.
  const q = filter.trim().toLowerCase();
  const visible = useMemo(() => {
    if (!q || emailMode) return sorted;
    return sorted.filter((c) => channelDisplay(c, currentUserId).name.toLowerCase().includes(q));
  }, [sorted, q, emailMode, currentUserId]);

  const openConversation = (id) => {
    setActiveChannel(id);
    clearUnread(id);
  };

  // ── Row context menu (Mute/Unmute, Leave channel) ─────────────────────────
  // `{ x, y, channelId }` rather than the channel object: the row must reflect
  // the LIVE channel while the menu is open, or muting from the menu and then
  // reopening it would still say "Mute" (the label reads `menuChannel.muted`).
  const [menu, setMenu] = useState(null);
  const [confirmLeaveId, setConfirmLeaveId] = useState(null);
  const [confirmDeleteDmId, setConfirmDeleteDmId] = useState(null);
  // Prefer the LIVE row from the store so the mute label follows a change made
  // while the menu is open — but fall back to the channel captured at click
  // time. Rendering nothing when the lookup misses turns any id mismatch
  // (type, a row not yet in `channels`, a refetch mid-open) into a menu that
  // silently does not appear, which is indistinguishable from "this feature is
  // not built" and is exactly how it was reported.
  const menuChannel = useMemo(
    () => (menu ? channels.find((c) => c.id === menu.channelId) || menu.channel || null : null),
    [menu, channels]
  );
  const leaveChannelRow = useMemo(
    () => (confirmLeaveId ? channels.find((c) => c.id === confirmLeaveId) || null : null),
    [confirmLeaveId, channels]
  );

  const closeMenu = useCallback(() => setMenu(null), []);

  const onRowContextMenu = (e, channel) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ x: e.clientX, y: e.clientY, channelId: channel.id, channel });
  };

  const onMenuSelect = (itemId) => {
    const channel = menuChannel;
    if (!channel) return;
    if (itemId === 'mute') {
      // Plain toggle — no confirmation, nothing to undo. The label already
      // told the user which direction they were going.
      setChannelMuted(channel.id, !isChannelMuted(channel));
      return;
    }
    if (itemId === 'delete-dm') {
      // Irreversible and it destroys files, so it is confirmed like Leave —
      // more so, since there is no invite that could bring this one back.
      setConfirmDeleteDmId(channel.id);
      return;
    }
    if (itemId === 'leave') {
      // Deliberately NOT one-click. This sits a few pixels below "Mute" in a
      // right-click menu, it cannot be undone without an invite from a member,
      // and it additionally destroys this device's cached copy of the history.
      // `ChannelMembersPopover`'s own Leave is gated the same way.
      setConfirmLeaveId(channel.id);
    }
  };

  // Leaving as the last member is not "leaving", it is a delete — the server
  // destroys the channel and purges its files. The confirmation has to say
  // that, because the click is identical either way.
  const leavingDeletes = leavingDestroysChannel(leaveChannelRow, currentUserId);

  const confirmLeave = () => {
    const id = confirmLeaveId;
    setConfirmLeaveId(null);
    if (!id || currentUserId == null) return;
    // Nothing local happens unless the server accepted the departure — the
    // list removal and the cache purge live behind that await in
    // services/chatRowActions.js, and a failure only raises a toast.
    leaveChannel(id, currentUserId);
  };

  const confirmDeleteDm = () => {
    const id = confirmDeleteDmId;
    setConfirmDeleteDmId(null);
    if (!id) return;
    deleteOrphanedDm(id);
  };

  const onChannelCreated = (channel) => {
    upsertChannel(channel);
    setNewChannelOpen(false);
    openConversation(channel.id);
  };

  return (
    <div className="chat-sidebar" style={{ position: 'relative' }}>
      {/* Fixed filter header — mirrors ExtensionSidebarSections/Collections.
          A complete email opens the people dropdown anchored below it. */}
      <div
        ref={searchWrapRef}
        className="flex-shrink-0 px-2 pt-2 border-b border-gray-200"
        style={{ position: 'relative' }}
      >
        <div className="sidebar-panel-search-row">
          <Search size={14} className="text-gray-400 flex-shrink-0" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search or start a DM by email…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={onSearchKeyDown}
            onFocus={() => {
              if (emailMode) setOpen(true);
            }}
            className="tab-dropdown-search-input min-w-0 flex-1 bg-transparent text-gray-900 placeholder-gray-400 border-none p-0 focus:outline-none"
            autoComplete="off"
            aria-label="Search conversations"
          />
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
            {filter ? (
              <button
                type="button"
                onClick={clearSearch}
                className="flex h-full w-full items-center justify-center rounded bg-transparent p-0 cursor-pointer text-gray-900 hover:opacity-70 focus:outline-none"
                aria-label="Clear filter"
                onMouseDown={(e) => e.preventDefault()}
              >
                <X size={14} strokeWidth={2} aria-hidden />
              </button>
            ) : null}
          </div>
        </div>

        {showDropdown && (
          <div className="titlebar-search-dropdown" role="listbox" style={{ left: 8, right: 8 }}>
            {results.map((u) => (
              <button
                key={u.id}
                type="button"
                className="titlebar-search-result"
                disabled={busy}
                onClick={() => openDm(u.id)}
              >
                <UserAvatar className="chat-avatar chat-avatar--sm" userId={u.id} name={u.name} size={28} />
                <span style={{ minWidth: 0 }}>
                  <span className="titlebar-search-result-name" style={{ display: 'block' }}>
                    {u.name}
                  </span>
                  <span className="titlebar-search-result-email" style={{ display: 'block' }}>
                    {u.email}
                  </span>
                </span>
              </button>
            ))}
            {error ? (
              <div className="titlebar-search-hint" style={{ color: '#ef4444' }}>{error}</div>
            ) : loading && results.length === 0 ? (
              <div className="titlebar-search-hint">Searching…</div>
            ) : !loading && results.length === 0 ? (
              <div className="titlebar-search-hint">No user found with that email address.</div>
            ) : null}
          </div>
        )}
      </div>

      <div className="chat-conv-list">
        {!channelsLoaded && sorted.length === 0 ? (
          // First list fetch still in flight — nothing during the instaload
          // grace window, then skeleton rows for genuinely slow loads. Either
          // way the "no conversations" copy can't flash at someone who has
          // plenty.
          showListSkeleton ? (
            <div className="chat-skel" aria-hidden="true">
              {[88, 64, 76, 52, 70, 58].map((w, i) => (
                <div key={i} className="chat-skel-conv-row">
                  <span className="chat-skel-shimmer chat-skel-conv-avatar" />
                  <span className="chat-skel-shimmer chat-skel-bar" style={{ width: w }} />
                </div>
              ))}
            </div>
          ) : null
        ) : sorted.length === 0 ? (
          <div style={{ padding: '16px 12px', fontSize: 13, color: '#9ca3af' }}>
            {ownsNoChannels
              ? t('chat.emptyFree')
              : 'No conversations yet. Use “New channel” below to create one, or type an email in the search to start a DM.'}
          </div>
        ) : visible.length === 0 ? (
          <div style={{ padding: '16px 12px', fontSize: 13, color: '#9ca3af' }}>
            No conversations match your search.
          </div>
        ) : (
          visible.map((c) => {
            const { name, seed } = channelDisplay(c, currentUserId);
            const unread = unreadByChannel[c.id] || 0;
            const badge =
              unread > 0 ? <span className="chat-avatar-badge">{unread > 99 ? '99+' : unread}</span> : null;
            return (
              <div
                key={c.id}
                className={`chat-conv-row ${c.id === activeChannelId ? 'chat-conv-row--active' : 'hover:bg-gray-50'}`}
                onClick={() => openConversation(c.id)}
                onContextMenu={(e) => onRowContextMenu(e, c)}
              >
                {c.kind === 'dm' ? (
                  <UserAvatar className="chat-avatar" userId={dmOther(c, currentUserId)?.user_id} name={name}>
                    {badge}
                    {/* Status only on 1:1 rows: a group has no single person
                        whose presence the row could describe. `.chat-avatar` is
                        34px in Chat.css. The unread badge moved to the TOP right
                        (see Chat.css) so the two no longer share this corner. */}
                    <AvatarStatusBadge
                      userId={dmSubjectId(c, currentUserId)}
                      avatarSize={34}
                    />
                  </UserAvatar>
                ) : (
                  <ChannelAvatar className="chat-avatar" channel={c} name={name} seed={seed}>
                    {badge}
                  </ChannelAvatar>
                )}
                <span className="chat-conv-name">{name}</span>
                {isChannelMuted(c) && (
                  <BellOff
                    size={12}
                    className="chat-conv-muted-icon"
                    aria-label={t('chat.menu.mutedIndicator')}
                  />
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer action — same style as the FTP sidebar's "New connection".
          Owning a channel is a paid feature (owner decision, 16.09.2026). The
          server refuses it either way; this is so a Free account is not walked
          into a dead end, and is told what it DOES have — direct messages and
          any channel someone invites them to. */}
      <div className="chat-sidebar-footer">
        <button
          type="button"
          className="chat-new-channel-btn"
          disabled={ownsNoChannels}
          title={ownsNoChannels ? t('chat.channelsArePaid') : undefined}
          onClick={() => setNewChannelOpen(true)}
        >
          <Plus size={13} /> {t('chat.newChannel', { defaultValue: 'New channel' })}
        </button>
        {ownsNoChannels && <div className="chat-sidebar-footer-note">{t('chat.channelsArePaid')}</div>}
      </div>

      <NewChannelModal
        isOpen={newChannelOpen}
        onClose={() => setNewChannelOpen(false)}
        onCreated={onChannelCreated}
      />

      {menu && menuChannel && (
        <ChatRowContextMenu
          x={menu.x}
          y={menu.y}
          channel={menuChannel}
          t={t}
          currentUserId={currentUserId}
          onSelect={onMenuSelect}
          onClose={closeMenu}
        />
      )}

      {/* Leaving is not undoable without an invite — say so, and make Cancel
          the safe default (nothing is autofocused, Escape closes). */}
      <ConfirmModal
        isOpen={Boolean(leaveChannelRow)}
        onClose={() => setConfirmLeaveId(null)}
        onConfirm={confirmLeave}
        title={t(leavingDeletes ? 'chat.leaveChannel.titleLast' : 'chat.leaveChannel.title')}
        message={t(leavingDeletes ? 'chat.leaveChannel.messageLast' : 'chat.leaveChannel.message', {
          name: leaveChannelRow ? channelDisplay(leaveChannelRow, currentUserId).name : '',
        })}
        confirmText={t(leavingDeletes ? 'chat.leaveChannel.confirmLast' : 'chat.leaveChannel.confirm')}
        cancelText={t('common.cancel')}
        type="danger"
      />

      {/* Deleting an orphaned DM destroys the messages AND the uploaded files,
          on the server, for good. There is no invite that brings this one
          back — the other account no longer exists — so the copy says exactly
          what goes. */}
      <ConfirmModal
        isOpen={Boolean(confirmDeleteDmId)}
        onClose={() => setConfirmDeleteDmId(null)}
        onConfirm={confirmDeleteDm}
        title={t('chat.menu.deleteDmTitle')}
        message={t('chat.menu.deleteDmBody')}
        confirmText={t('chat.menu.deleteDm')}
        cancelText={t('common.cancel')}
        type="danger"
      />
    </div>
  );
}
